var __spreadArrays = (this && this.__spreadArrays) || function () {
    for (var s = 0, i = 0, il = arguments.length; i < il; i++) s += arguments[i].length;
    for (var r = Array(s), k = 0, i = 0; i < il; i++)
        for (var a = arguments[i], j = 0, jl = a.length; j < jl; j++, k++)
            r[k] = a[j];
    return r;
};
import * as R from 'ramda';
import { XbpmBlockType } from '../../models';
import { updateElementProperty, updateElementWith } from '../../services/utils/def.utils';
import { isElementBlock } from '../../services/utils/_.utils';
import { isHttpError, mapInternalError, isCancelation } from '../../services/utils/result.utils';
// map form path
export var props2FormPath = function (processInstanceId, taskDefinitionKey, modalFormCode) { return __spreadArrays([
    'items',
    processInstanceId,
    taskDefinitionKey
], (!modalFormCode ? ['form'] : ['children', modalFormCode])); };
// current form marker utils
export var eqMarkers = R.eqBy(R.pickAll(['taskDefinitionKey', 'processInstanceId', 'modalFormCode']));
export var marker2FormPath = function (marker) {
    return !marker || !marker.taskDefinitionKey
        ? []
        : props2FormPath(marker.processInstanceId, marker.taskDefinitionKey, marker.modalFormCode);
};
export var getMarker = R.curry(function (taskDefinitionKey, processInstanceId, modalFormCode, state) {
    return R.findLast(eqMarkers({ taskDefinitionKey: taskDefinitionKey, processInstanceId: processInstanceId, modalFormCode: modalFormCode || null }), state.markers);
});
// change form marker
export var changeMarker = function (processInstanceId, taskDefinitionKey, modalFormCode, initiatorId, parent) { return function (state) {
    var marker = getMarker(taskDefinitionKey, processInstanceId, modalFormCode, state) || {
        taskDefinitionKey: taskDefinitionKey,
        processInstanceId: processInstanceId,
        modalFormCode: modalFormCode || null,
        initiatorId: initiatorId,
        parent: parent,
    };
    var markerIdx = state.markers.findIndex(eqMarkers(marker));
    var markers = markerIdx === -1 ? R.append(marker, state.markers) : R.update(markerIdx, marker, state.markers);
    return R.pipe(R.assoc('marker', marker), R.assoc('markers', markers))(state);
}; };
// form status flags utils
export var setInternalStatus = R.curry(function (status, state) { return R.assoc('internalStatus', status, state); });
var setBusyStatus = R.curry(function (busyModeProp, val, state) {
    return R.pipe(R.assoc(busyModeProp, val), R.when(R.always(!!val), R.pipe(setError(null), setInternalStatus(null))), R.when(R.always(!val && !state.internalStatus), setInternalStatus('ok')))(state);
});
export var setLoading = setBusyStatus('isLoading');
export var setSubmitting = setBusyStatus('isSubmitting');
var prepareError = function (err) {
    return !err || isCancelation(err) ? null : isHttpError(err) ? err : mapInternalError('', err);
};
export var setError = R.curry(function (err, state) {
    return R.pipe(R.assoc('error', prepareError(err)), R.when(R.always(!!err), setInternalStatus('err')))(state);
});
// load form result
export var loadSuccessResult = function (def, processInstanceId, taskDefinitionKey, state) {
    return R.pipe(R.assocPath(['items', processInstanceId, taskDefinitionKey, 'form'], def), changeMarker(processInstanceId, taskDefinitionKey, null, null, null), setLoading(false), setSubmitting(false))(state);
};
// save form result
export var saveSuccessResult = function (def, processInstanceId, taskDefinitionKey, state) {
    return R.pipe(R.assocPath(['items', processInstanceId, taskDefinitionKey, 'form'], def), setSubmitting(false))(state);
};
// form refresh/update block result
export var refreshSuccessResult = function (def, processInstanceId, taskDefinitionKey, modalFormCode, state) {
    return R.pipe(R.assocPath(props2FormPath(processInstanceId, taskDefinitionKey, modalFormCode), def), setLoading(false))(state);
};
// child form load result
export var childLoadSuccessResult = function (def, processInstanceId, taskDefinitionKey, modalFormCode, initiatorId, state) {
    return R.pipe(R.assocPath(['items', processInstanceId, taskDefinitionKey, 'children', modalFormCode], def), changeMarker(processInstanceId, taskDefinitionKey, modalFormCode, initiatorId, state.marker), setLoading(false))(state);
};
// save child form result
export var childSaveSuccessResult = function (def, processInstanceId, taskDefinitionKey, modalFormCode, result, state) {
    var marker = getMarker(taskDefinitionKey, processInstanceId, modalFormCode, state);
    var parentPath = marker ? marker2FormPath(marker.parent) : [];
    var parentNext = R.when(R.complement(R.isNil), function (form) { return updateElementProperty(marker.initiatorId, 'value', result, form); }, parentPath.length ? R.path(parentPath, state) : null);
    return R.pipe(R.assocPath(['items', processInstanceId, taskDefinitionKey, 'children', modalFormCode], def), parentNext ? R.assocPath(parentPath, parentNext) : R.identity, setSubmitting(false))(state);
};
// add block result
export var addBlockSuccessResult = function (processInstanceId, taskDefinitionKey, modalFormCode, inputBlockId, prevBlockId, newBlock, state) {
    var formPath = props2FormPath(processInstanceId, taskDefinitionKey, modalFormCode);
    return R.pipe(R.path(formPath), function (form) {
        return updateElementWith(function (it) {
            return isElementBlock(it) &&
                // append to block
                ((it.id === inputBlockId && !(it.elements.length === 1 && it.elements[0].kind === XbpmBlockType.PANEL)) ||
                    // append to child panel
                    it.parentId === inputBlockId);
        }, R.pipe(R.over(R.lensProp('elements'), function (items) {
            var index = prevBlockId ? R.findIndex(R.propEq('id', prevBlockId), items) + 1 : 0;
            return R.insert(index, newBlock, items);
        }), R.assoc('lastChanged', Date.now())), form);
    }, R.assocPath(formPath, R.__, state), setSubmitting(false))(state);
};
// remove block result
export var removeBlockSuccessResult = function (processInstanceId, taskDefinitionKey, modalFormCode, blockId, state) {
    var formPath = props2FormPath(processInstanceId, taskDefinitionKey, modalFormCode);
    return R.pipe(R.path(formPath), function (form) {
        return updateElementWith(R.pipe(R.propOr([], 'elements'), R.find(R.propEq('id', blockId))), R.pipe(R.over(R.lensProp('elements'), R.reject(R.propEq('id', blockId))), R.assoc('lastChanged', Date.now())), form);
    }, R.assocPath(formPath, R.__, state), setSubmitting(false))(state);
};
// click request result
export var clickRequestSuccessResult = function (processInstanceId, taskDefinitionKey, modalFormCode, initiatorId, result, state) {
    var marker = getMarker(taskDefinitionKey, processInstanceId, modalFormCode, state);
    var parentPath = marker ? marker2FormPath(marker) : [];
    var parentNext = R.when(R.complement(R.isNil), function (form) { return updateElementProperty(initiatorId, 'value', result, form); }, parentPath.length ? R.path(parentPath, state) : null);
    return R.pipe(parentNext ? R.assocPath(parentPath, parentNext) : R.identity, setSubmitting(false))(state);
};
//# sourceMappingURL=reducer-helper.js.map